/**
 * AngularJS service wrapper for the Preload Bridge Client.
 *
 * Thin delegation layer: the heavy lifting (postMessage, status replay,
 * listener fan-out) lives in the injected PreloadBridgeClient implementation.
 * In PWA mode that's PWAPreloadBridgeClient; in native mode it's the no-op
 * stub registered alongside the other bridge clients.
 *
 * Taking the client via DI instead of `new`-ing one here avoids creating a
 * second client instance — otherwise the app would register two `message`
 * listeners on the service worker and fire two initial status requests.
 */
class PreloadBridgeService {
    constructor(PreloadBridgeClient) {
        this.client = PreloadBridgeClient;
    }

    /**
     * Register a callback to receive preload status updates
     * @param {Function} callback - Callback function receiving PreloadStatusMessage
     */
    onStatusUpdate(callback) {
        this.client.onStatusUpdate(callback);
    }

    /**
     * Remove a status update callback
     * @param {Function} callback - The callback to remove
     */
    offStatusUpdate(callback) {
        this.client.offStatusUpdate(callback);
    }

    /**
     * Get the current preload state
     * @returns {string} Current state: 'idle', 'checking', 'downloading', 'ready', or 'error'
     */
    getState() {
        return this.client.getState();
    }

    /**
     * Get the current status message
     * @returns {Object|null} Last received status message
     */
    getStatus() {
        return this.client.getStatus();
    }

    /**
     * Request the service worker to start/resume preloading
     */
    startPreload() {
        this.client.startPreload();
    }

    /**
     * Request the service worker to force refresh all bundles
     */
    forceRefresh() {
        this.client.forceRefresh();
    }

    /**
     * Ask the service worker to download one or more optional bundles if they
     * are not already up-to-date. Used to opt in to on-demand assets like the
     * specialty-font bundle once the app knows it will need them.
     *
     * Safe to call repeatedly; the service worker dedupes and skips bundles
     * that already match the manifest hash.
     *
     * @param {string[]} bundleNames - e.g. ['fonts-special']
     */
    ensureBundles(bundleNames) {
        this.client.ensureBundles(bundleNames);
    }

    /**
     * Request current status from service worker
     */
    requestStatus() {
        this.client.requestStatus();
    }

    /**
     * Check if the preload system is usable in the current runtime.
     * In native mode (no service worker) the injected client is a no-op
     * stub — callers use this flag to skip the entire preload flow rather
     * than send messages into the void.
     * @returns {boolean}
     */
    isAvailable() {
        return 'serviceWorker' in navigator;
    }
}

PreloadBridgeService.$inject = ['PreloadBridgeClient'];

export default PreloadBridgeService;
